import { ChevronDown, HelpCircle } from 'lucide-react';

/**
 * Subtle, collapsible "How this works" card used across the app.
 *
 *   <HelpSection title="💡 How Your Dashboard Works">
 *     …rich markdown-like content via <HelpP>, <HelpList>, <HelpRule>…
 *   </HelpSection>
 *
 * Styling: matches the dark theme, never disrupts layout. Uses a native
 * <details> element so it's accessible, keyboard-operable, and zero-JS.
 */
export function HelpSection({ title, children, defaultOpen = false, testId = 'help-section' }) {
  return (
    <details
      className="group bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm transition-colors open:shadow-md"
      data-testid={testId}
      {...(defaultOpen ? { open: true } : {})}
    >
      <summary
        className="flex items-center justify-between gap-3 px-5 py-3 cursor-pointer list-none select-none rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/80"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <HelpCircle className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" aria-hidden="true" />
          {title}
        </span>
        <ChevronDown
          className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-180 flex-shrink-0"
          aria-hidden="true"
        />
      </summary>
      <div className="px-5 pb-5 pt-1 text-sm text-gray-700 dark:text-gray-300 space-y-3 leading-relaxed">
        {children}
      </div>
    </details>
  );
}

/** Paragraph spacer. Accepts `muted` for secondary tone. */
export function HelpP({ children, muted = false }) {
  return (
    <p className={muted ? 'text-gray-600 dark:text-gray-400' : ''}>{children}</p>
  );
}

/** Labeled bullet list. Items should be strings or React nodes. */
export function HelpList({ items }) {
  return (
    <ul className="space-y-1 list-disc list-inside marker:text-gray-400 dark:marker:text-gray-500">
      {items.map((item, i) => (
        // Items are static JSX/strings declared inline in help panels — they
        // never reorder. Use content as key when it's a primitive, else index
        // with a stable prefix so React isn't confused between lists.
        <li key={typeof item === 'string' ? item : `help-list-${i}`}>{item}</li>
      ))}
    </ul>
  );
}

/** Numbered list — for ordered flows ("1. Income comes in …"). */
export function HelpSteps({ items }) {
  return (
    <ol className="space-y-1 list-decimal list-inside marker:text-gray-500 dark:marker:text-gray-400">
      {items.map((item, i) => (
        <li key={typeof item === 'string' ? item : `help-step-${i}`}>{item}</li>
      ))}
    </ol>
  );
}

/** Subtle divider between sub-sections inside the help card. */
export function HelpRule() {
  return <div className="border-t border-gray-200/60 dark:border-gray-700/60 my-2" aria-hidden="true" />;
}

/** Emphasized callout — the "Simple rule" / "Goal" block at the bottom of most help panels. */
export function HelpCallout({ tone = 'emerald', children }) {
  const tones = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-800 dark:text-emerald-200',
    amber:   'bg-amber-500/10 border-amber-500/30 text-amber-800 dark:text-amber-200',
    blue:    'bg-blue-500/10 border-blue-500/30 text-blue-800 dark:text-blue-200',
  };
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${tones[tone] || tones.emerald}`}>
      {children}
    </div>
  );
}

/** Small sub-heading inside a help card ("🧠 Core rule:", "📊 Modes:" …). */
export function HelpHeading({ children }) {
  return (
    <p className="font-semibold text-gray-900 dark:text-gray-100 mt-1">{children}</p>
  );
}

export default HelpSection;
